"use client";
import React, { useRef, useState } from "react";
import { m, useScroll, useTransform, AnimatePresence } from "motion/react";
import { cn } from "@/lib/utils";
import { IconArrowUpRight, IconChevronLeft, IconChevronRight } from "@tabler/icons-react";
import Image from "next/image";

// ─── DATA ─────────────────────────────────────────────────────────────────────

const cases = [
    {
        image: "/foto-ney.webp",
        result: "+340%",
        metric: "Crescimento em leads qualificados",
        description:
            "Instituto que não conseguia sair do boca a boca. Em 4 meses, com tráfego pago e automação de WhatsApp, triplicamos a agenda de consultas.",
        company: "Instituto Ney Simões",
        segment: "Saúde & Bem-estar",
        tags: ["Tráfego Pago", "Automação", "CRM"],
    },
    {
        image: "/logo-comprano.png",
        result: "R$ 300K",
        metric: "Em receita gerada para o e-commerce",
        description:
            "E-commerce de petshop que precisava escalar vendas online. Estruturamos tráfego pago, criativos e recuperação de carrinho abandonado.",
        company: "Comprano",
        segment: "Pet Shop Online",
        tags: ["Meta Ads", "Google Ads", "CRO"],
    },
    {
        image: "/logo-duckbill.webp",
        result: "5x",
        metric: "ROI sobre investimento em mídia",
        description:
            "Franquia de cafeteria que precisava atrair novos franqueados e aumentar o movimento nas unidades. Combinamos tráfego pago com estratégia de expansão de marca.",
        company: "Duckbill",
        segment: "Franquia de Cafeteria",
        tags: ["Expansão de Marca", "Franqueados", "OOH"],
    },
];

const testimonials = [
    {
        image: "/foto-ney.webp",
        text: "O que me chamou a atenção na Midas foi a velocidade. Eles sentaram com a gente, entenderam nossa rotina de ponta a ponta e na mesma semana o telefone aqui do Instituto não parava de tocar com paciente qualificado. É outra pegada.",
        name: "Dr. Ney Simões",
        role: "CEO",
        company: "Instituto Ney Simões",
    },
    {
        image: "/denissonpaiva.jpg",
        text: "A gente sempre sofreu com agência que só entregava post bonito mas esquecia de olhar pro faturamento do salão. O pessoal da Midas senta junto pra olhar o negócio. Os caras respiram vendas junto com a gente.",
        name: "Chef Denisson Paiva",
        role: "Sócio-fundador",
        company: "Restaurante Ferro e Fogo",
    },
    {
        image: "/cacau.jpg",
        text: "Fechamos o projeto e em menos de dois meses a conta já estava se pagando. Mas o melhor mesmo é não ficar no escuro; aqueles dashboards que eles montaram mudaram o jeito que eu olho pra nossa operação hoje.",
        name: "Cacau Ventura",
        role: "Executivo da Band Nordeste",
        company: "Band TV",
    },
];

// ─── MAIN COMPONENT ────────────────────────────────────────────────────────────

export default function CasesSection() {
    const containerRef = useRef<HTMLDivElement>(null);
    const { scrollYProgress } = useScroll({
        target: containerRef,
        offset: ["start end", "end start"],
    });

    const headerY = useTransform(scrollYProgress, [0, 0.4], [40, 0]);
    const headerOpacity = useTransform(scrollYProgress, [0, 0.2], [0, 1]);

    return (
        <section ref={containerRef} className="py-28 bg-neutral-900 relative overflow-hidden">
            {/* Subtle grid background */}
            <div className="absolute inset-0 bg-[url('/grid.svg')] opacity-10 [mask-image:radial-gradient(ellipse_at_center,white,transparent)] pointer-events-none" />

            {/* Gold radial glow */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-midas-yellow-500/5 blur-[120px] rounded-full pointer-events-none" />

            <div className="max-w-7xl mx-auto px-6 relative z-10">
                {/* ── Section Header ── */}
                <m.div
                    style={{ y: headerY, opacity: headerOpacity }}
                    className="mb-20"
                >
                    <span className="text-midas-yellow-500 font-mono text-xs tracking-[4px] uppercase mb-4 block">
                        Resultados Reais
                    </span>
                    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
                        <h2 className="text-4xl md:text-6xl font-bold text-white leading-tight max-w-xl">
                            Cases que{" "}
                            <span className="text-midas-yellow-500">provam</span>{" "}
                            nossa entrega
                        </h2>
                        <p className="text-neutral-400 text-lg max-w-sm leading-relaxed">
                            Não vendemos promessas. Vendemos resultados documentados.
                        </p>
                    </div>
                    {/* Gold divider */}
                    <div className="w-16 h-px bg-midas-yellow-500 mt-8" />
                </m.div>

                {/* ── Cases Grid ── */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-24">
                    {cases.map((c, i) => (
                        <CaseCard key={i} {...c} index={i} />
                    ))}
                </div>

                {/* ── Testimonials Header ── */}
                <m.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="mb-12"
                >
                    <span className="text-midas-yellow-500 font-mono text-xs tracking-[4px] uppercase mb-4 block">
                        O que nossos clientes dizem
                    </span>
                    <h3 className="text-3xl md:text-4xl font-bold text-white">
                        Vozes que{" "}
                        <span className="text-midas-yellow-500">importam</span>
                    </h3>
                </m.div>

                {/* ── Testimonials Grid ── */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {testimonials.map((t, i) => (
                        <TestimonialCard key={i} {...t} index={i} />
                    ))}
                </div>
            </div>
        </section>
    );
}

// ─── CASE CARD ─────────────────────────────────────────────────────────────────

function CaseCard({
    image,
    result,
    metric,
    description,
    company,
    segment,
    tags,
    index,
}: {
    image: string;
    result: string;
    metric: string;
    description: string;
    company: string;
    segment: string;
    tags: string[];
    index: number;
}) {
    return (
        <m.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: index * 0.12, duration: 0.55, ease: "easeOut" }}
            whileHover={{ y: -6 }}
            className="group relative p-8 rounded-2xl bg-black border border-neutral-800 hover:border-midas-yellow-500/40 transition-all duration-300 hover:shadow-[0_0_40px_-12px_rgba(245,197,24,0.2)] flex flex-col"
        >
            {/* Top gold line on hover */}
            <m.div
                className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-midas-yellow-500 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"
            />

            {/* Header row */}
            <div className="flex items-center justify-between mb-6">
                <div className="relative w-24 h-24 rounded-xl bg-neutral-900 border border-neutral-700 group-hover:border-midas-yellow-500/30 overflow-hidden transition-colors duration-300">
                    <Image src={image} alt={company} fill className="object-cover" />
                </div>
                <IconArrowUpRight className="w-5 h-5 text-neutral-700 group-hover:text-midas-yellow-500 transition-colors duration-300" />
            </div>

            {/* Result */}
            <div className="mb-1">
                <span className="font-bold text-midas-yellow-500 leading-none block"
                    style={{ fontSize: "clamp(2.5rem, 5vw, 3.5rem)", lineHeight: 1 }}>
                    {result}
                </span>
            </div>
            <p className="text-neutral-500 text-xs uppercase tracking-[2px] font-medium mb-5">
                {metric}
            </p>

            {/* Description */}
            <p className="text-neutral-400 text-sm leading-relaxed flex-1 mb-6">
                {description}
            </p>

            {/* Footer */}
            <div className="border-t border-neutral-800 pt-5">
                <p className="text-white font-semibold text-sm">{company}</p>
                <p className="text-neutral-600 text-xs mt-1">{segment}</p>

                {/* Tags */}
                <div className="flex flex-wrap gap-2 mt-4">
                    {tags.map((tag) => (
                        <span
                            key={tag}
                            className="text-[11px] font-medium uppercase tracking-wider px-3 py-1 rounded-full bg-midas-yellow-500/8 border border-midas-yellow-500/20 text-midas-yellow-500/80"
                        >
                            {tag}
                        </span>
                    ))}
                </div>
            </div>
        </m.div>
    );
}

// ─── TESTIMONIAL CARD ──────────────────────────────────────────────────────────

function TestimonialCard({
    image,
    initials,
    text,
    name,
    role,
    company,
    index,
}: {
    image?: string;
    initials?: string;
    text: string;
    name: string;
    role: string;
    company: string;
    index: number;
}) {
    return (
        <m.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: index * 0.1, duration: 0.5, ease: "easeOut" }}
            whileHover={{ y: -4 }}
            className="group p-8 rounded-2xl bg-black border border-neutral-800 hover:border-midas-yellow-500/30 transition-all duration-300 hover:shadow-[0_0_30px_-10px_rgba(245,197,24,0.15)] flex flex-col"
        >
            {/* Stars */}
            <div className="flex gap-1 mb-6">
                {Array.from({ length: 5 }).map((_, i) => (
                    <m.span
                        key={i}
                        initial={{ opacity: 0, scale: 0.5 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ delay: index * 0.1 + i * 0.05, duration: 0.3 }}
                        className="text-midas-yellow-500 text-sm"
                    >
                        ★
                    </m.span>
                ))}
            </div>

            {/* Quote mark */}
            <div className="text-midas-yellow-500/20 font-serif text-7xl leading-none mb-2 select-none">
                &ldquo;
            </div>

            {/* Text */}
            <p className="text-neutral-300 text-sm leading-relaxed italic flex-1 -mt-8">
                {text}
            </p>

            {/* Author */}
            <div className="flex items-center gap-4 mt-8 pt-6 border-t border-neutral-800">
                <div className="relative w-10 h-10 rounded-full bg-gradient-to-br from-neutral-700 to-neutral-900 border border-neutral-700 group-hover:border-midas-yellow-500/40 overflow-hidden flex items-center justify-center font-bold text-midas-yellow-500 text-xs tracking-wider flex-shrink-0 transition-colors duration-300">
                    {image ? (
                        <Image src={image} alt={name} fill className="object-cover" />
                    ) : (
                        initials
                    )}
                </div>
                <div>
                    <p className="text-white font-semibold text-sm">{name}</p>
                    <p className="text-neutral-500 text-xs mt-0.5">
                        {role} · {company}
                    </p>
                </div>
            </div>
        </m.div>
    );
}
